import { Sheet } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChangeEvent, VFC, useEffect, useState } from "react";
import { ReportsCard } from "../components/ReportsCard";
import { NavigationTabs } from "@/components/ui/navigation-tabs";
import { PagePagination } from "@/components/ui/page-pagination";
import { PageWithTitle } from "@common/components/PageLayout/PageWithTitle";
import { CreateReimbursementSheet } from "../sheets/CreateReimbursementSheet";
import { ReportsTrigger } from "../admin/AdminReports";
import {
  EmployeeReportsSelectedTab,
  useEmployeeReportsTabs,
} from "./useEmployeeReportsTabs";
import { useEmployeeReportsData } from "./useEmployeeReportsData";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { ViewOrEditReportSheet } from "../sheets/ViewOrEditReportSheet";
import { ClockClockwise, FileDashed } from "@phosphor-icons/react";
import { NoResultsCard } from "@common/components/NoResultsCard";
import { AddBankAccountBanner } from "@common/components/AddBankAccountBanner";

interface Props {
  tab: EmployeeReportsSelectedTab;
}

const noResultsContent = {
  [EmployeeReportsSelectedTab.Draft]: {
    Icon: FileDashed,
    header: "No draft reports",
    body: "You don't have any reports created.",
  },
  [EmployeeReportsSelectedTab.Pending]: {
    Icon: ClockClockwise,
    header: "No pending reimbursements",
    body: "You have not submitted any new transactions or reports.",
  },
  [EmployeeReportsSelectedTab.Resolved]: {
    Icon: FileDashed,
    header: "No approved or denied reports",
    body: "No reports have been approved or denied yet.",
  },
};
export const Reports: VFC<Props> = ({ tab }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const { tabs, status } = useEmployeeReportsTabs(tab, setCurrentPage);
  const { loadingReports, reports, hasMorePages, refetchReports } =
    useEmployeeReportsData(searchText, currentPage, status);

  // go to top of page when next/prev page is pressed
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const [selectedReport, setSelectedReport] = useState<
    ReportsTrigger | undefined
  >(undefined);

  const resetSelectedReport = () => setSelectedReport(undefined);

  const [isCreateReimbursementSheetOpen, setIsCreateReimbursementSheetOpen] =
    useState(false);

  const handleSearchOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(0);
  };

  const searchHeader = (
    <div className="flex justify-between pt-5">
      <div className="flex gap-4">
        <Input
          className="w-[250px]"
          type="search"
          placeholder="Search by report name"
          onChange={handleSearchOnChange}
        />
      </div>
      {tab === EmployeeReportsSelectedTab.Draft && (
        <Button onClick={() => setIsCreateReimbursementSheetOpen(true)}>
          Create Reimbursement
        </Button>
      )}
    </div>
  );

  const results = reports.map((report) => (
    <ReportsCard report={report} onClick={setSelectedReport} key={report.id} />
  ));

  const pagination = !loadingReports && reports.length > 0 && (
    <PagePagination
      currentPage={currentPage}
      hasMorePages={hasMorePages}
      onChange={setCurrentPage}
    />
  );

  const noResults = <NoResultsCard {...noResultsContent[tab]} />;

  const sheets = (
    <>
      <CreateReimbursementSheet
        isOpen={isCreateReimbursementSheetOpen}
        onClose={() => {
          setIsCreateReimbursementSheetOpen(false);
          refetchReports();
        }}
      />
      <ViewOrEditReportSheet
        isOpen={!!selectedReport}
        onClose={resetSelectedReport}
        selectedReport={selectedReport!}
      />
    </>
  );

  return (
    <PageWithTitle title="Reports">
      <NavigationTabs tabs={tabs} selectedTab={tab} />
      {searchHeader}
      {loadingReports && reports.length === 0 ? (
        <FullPageLoader />
      ) : (
        <>
          <AddBankAccountBanner />
          <Sheet open={false}>{reports.length > 0 ? results : noResults}</Sheet>
        </>
      )}
      {pagination}
      {sheets}
    </PageWithTitle>
  );
};
